import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchTicketList } from "../../redux/actions/chatbot";
import { fmtDate, StatusWiseFooterText, getTicketStatusLabel } from "./Constants";
interface TicketItem {
  ticketId: string;
  ticketNumber: string | null;
  title: string;
  description: string;
  status: string;
  priority: string;
  createdAt: string;
  processStep?: string;
  ownerTeam?: string;
  updatedAt: string;
}

interface TicketListProps {
  tickets: TicketItem[];
  onBack: () => void;
  onTicketClick: (ticket: TicketItem) => void;
}

const formatSnakCaseString = (step?: string): string => {
  if (!step) return '';
  return step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const TicketList: React.FC<TicketListProps> = ({ tickets, onBack, onTicketClick }) => {
  const dispatch = useDispatch();

  // Fetch fresh ticket list on mount
  useEffect(() => {
    dispatch(fetchTicketList());
  }, [dispatch]);

  return (
    <div className="chat-ticket-list">
      {/* Header */}
      <div className="chat-panel-header cursor-pointer" onClick={onBack}>
        <div className="chat-panel-header-back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#AEAEAE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15.833 10H4.167M9.167 15L4.167 10l5-5" />
          </svg>
        </div>
        <div className="chat-panel-header-text">
          <div className="chat-panel-header-title">All tickets</div>
        </div>
        <div />{/* spacer */}
      </div>

      {/* Body */}
      <div className="chat-panel-body">
        {tickets.length === 0 ? (
          <div className="chat-ticket-empty">No tickets created yet</div>
        ) : (
          <div className="chat-ticket-section">
            {tickets.map((t, idx) => {
              const description = t.description?.split('\n\n')[0] || 'N/A';
              const slaText = StatusWiseFooterText[t.status?.toLocaleLowerCase() as keyof typeof StatusWiseFooterText](t);
              return (
                <React.Fragment key={t.ticketId || idx}>
                  {idx > 0 && <div className="chat-ticket-divider" />}
                  <div className="chat-ticket-item chat-ticket-item-clickable">
                    <div className="chat-ticket-item-header">
                      <div className="chat-ticket-item-header-left">
                        <span className="chat-ticket-item-number">Ticket #{t.ticketNumber || t.ticketId}</span>
                        <span className="chat-ticket-item-date">{fmtDate(new Date(t.createdAt))}</span>
                      </div>
                      <div className="chat-ticket-item-status-row">
                        <span className={`chat-ticket-status-dot chat-ticket-status-dot-${t.status.toLowerCase()}`} />
                        <span className="chat-ticket-item-status">{getTicketStatusLabel(t.status)}
                        </span>
                        <svg onClick={() => onTicketClick(t)} width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <mask id="path-1-inside-1_509_16015" fill="white">
                            <path d="M0 8C0 3.58172 3.58172 0 8 0H24C28.4183 0 32 3.58172 32 8V24C32 28.4183 28.4183 32 24 32H8C3.58172 32 0 28.4183 0 24V8Z" />
                          </mask>
                          <path d="M0 8C0 3.58172 3.58172 0 8 0H24C28.4183 0 32 3.58172 32 8V24C32 28.4183 28.4183 32 24 32H8C3.58172 32 0 28.4183 0 24V8Z" fill="white" />
                          <path d="M8 0V1H24V0V-1H8V0ZM32 8H31V24H32H33V8H32ZM24 32V31H8V32V33H24V32ZM0 24H1V8H0H-1V24H0ZM8 32V31C4.13401 31 1 27.866 1 24H0H-1C-1 28.9706 3.02944 33 8 33V32ZM32 24H31C31 27.866 27.866 31 24 31V32V33C28.9706 33 33 28.9706 33 24H32ZM24 0V1C27.866 1 31 4.13401 31 8H32H33C33 3.02944 28.9706 -1 24 -1V0ZM8 0V-1C3.02944 -1 -1 3.02944 -1 8H0H1C1 4.13401 4.13401 1 8 1V0Z" fill="#E8E8E3" mask="url(#path-1-inside-1_509_16015)" />
                          <path d="M10.5 16H21.5M17 20.5L21.5 16L17 11.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" stroke="#0C0C09" />
                        </svg>
                      </div>
                    </div>
                    <div className="chat-ticket-item-detail">
                      <span className="chat-ticket-item-detail-label">Category:</span>
                      <span className="chat-ticket-item-detail-value">{t.title || 'N/A'}</span>
                    </div>
                    <div className="chat-ticket-item-detail">
                      <span className="chat-ticket-item-detail-label">Step:</span>
                      <span className="chat-ticket-item-detail-value">{t.processStep ? formatSnakCaseString(t.processStep) : 'N/A'}</span>
                    </div>
                    <div className="chat-ticket-item-detail">
                      <span className="chat-ticket-item-detail-label">Issue description:</span>
                      <span className="chat-ticket-item-detail-value" title={description}>{description?.substring(0, 80) + (description && description.length > 80 ? '...' : '')}</span>
                    </div>
                    {slaText && <div className="chat-ticket-item-footer">
                      <span className="chat-ticket-item-footer-text">{slaText}</span>
                    </div>}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketList;
